import { useEffect, useMemo, useState } from 'react';
import { useGetCommentAttachmentMutation } from '../../../../../../api/gms/proposal-chat/proposal-chats-api';
import { getFileIcon, isImageFile } from '../../../../../ui/get-file-icon';
import { formatDateTimeChatMessage } from '../../../../../../utils/format-date-time-message.utils';
import ImageAttachmentPreviewModal from './image-attachment-preview-modal';
import FileAttachmentPreviewModal from './fiel-attachement-preview-modal';
import { useToastify } from '../../../../../../library/toastify/toast-provider';


export interface ChatAttachment {
    id: number;
    name: string;
    type: string;
    size: string;
    url?: string;
    messageId?: number;
    uploadedBy?: string;
    uploadedAt?: string;
}

interface Props {
    isOpen: boolean;
    attachments: ChatAttachment[];
    onClose: () => void;
    onDownload: (attachment: ChatAttachment) => void;
}

const VIEWABLE_FILE_EXTENSIONS = [
    'pdf',
    'txt',
    'csv',
    'json',
    'xml',
    'md',
    'log',
    'yaml',
    'yml',
    'mp3',
    'wav',
    'ogg',
    'm4a',
    'aac',
    'flac',
    'mp4',
    'webm',
    'mov',
];

const getFileExtension = (fileName = '') =>
    fileName.split('.').pop()?.toLowerCase() ?? '';

const isViewableFile = (fileName = '') =>
    isImageFile(fileName) ||
    VIEWABLE_FILE_EXTENSIONS.includes(getFileExtension(fileName));

export default function ChatAttachmentModal({ isOpen, attachments, onClose, onDownload, }: Props) {

    const { showToastNotify } = useToastify();
    const groupedAttachments = useMemo(() => attachments, [attachments]);
    const [getCommentAttachment] = useGetCommentAttachmentMutation();

    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [previewAttachment, setPreviewAttachment] = useState<ChatAttachment | null>(null);
    const [isLoadingPreview, setIsLoadingPreview] = useState(false);

    const isImagePreview = Boolean(
        previewAttachment && isImageFile(previewAttachment.name)
        );

    const handlePreview = async (attachment: ChatAttachment) => {
        if (!attachment.messageId || !isViewableFile(attachment.name)) return;

        setPreviewAttachment(attachment);
        setPreviewUrl(null);
        setIsLoadingPreview(true);

        try {
            const blob = await getCommentAttachment({
                commentId: attachment.messageId,
            }).unwrap();

            const objectUrl = URL.createObjectURL(blob);

            setPreviewUrl(objectUrl);
        } catch (error) {
            showToastNotify('Failed to load attachment preview', 'error')
            setPreviewAttachment(null);
        } finally {
            setIsLoadingPreview(false);
        }
    };

    const handleClosePreview = () => {
        setPreviewAttachment(null);
        setPreviewUrl((currentUrl) => {
            if (currentUrl) {
                URL.revokeObjectURL(currentUrl);
            }

            return null;
        });
        setIsLoadingPreview(false);
    };

    useEffect(() => {
        return () => {
            if (previewUrl) {
                URL.revokeObjectURL(previewUrl);
            }
        };
    }, [previewUrl]);

    useEffect(() => {
        if (!isOpen) {
            handleClosePreview();
        }
    }, [isOpen]);

    if (!isOpen) return null;

    return (
        <>
            <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 p-3 sm:p-4">
                <div className="flex max-h-[90dvh] w-full max-w-[620px] flex-col overflow-hidden rounded-xl bg-white shadow-2xl">
                    <div className="flex shrink-0 items-center justify-between border-b border-gray-200 px-4 py-3">
                        <div className="flex min-w-0 items-center gap-2">
                            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-[#186cb2]/10">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-4 w-4 text-[#186cb2]">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
                                </svg>
                            </div>
                            <div className="min-w-0">
                                <p className="truncate text-xs font-semibold text-gray-800">
                                    All Attachments
                                </p>
                                <p className="text-[10px] text-gray-500">
                                    {groupedAttachments.length} {groupedAttachments.length === 1 ? 'file' : 'files'} attached to this conversation
                                </p>
                            </div>
                        </div>
                        <button
                            type="button"
                            onClick={onClose}
                            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-gray-400 transition hover:bg-gray-100 hover:text-gray-600"
                            title="Close"
                        >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-4 w-4">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M6 6l12 12M18 6 6 18" />
                            </svg>
                        </button>
                    </div>
                    <div className="min-h-0 flex-1 overflow-y-auto p-3">
                        {groupedAttachments.length === 0 ? (
                            <div className="flex min-h-[220px] items-center justify-center">
                                <div className="text-center">
                                    <div className="mx-auto mb-2 flex h-11 w-11 items-center justify-center rounded-full bg-gray-100">
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-5 w-5 text-gray-400">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 1 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
                                        </svg>
                                    </div>
                                    <p className="text-xs font-medium text-gray-700">
                                        No attachments
                                    </p>
                                    <p className="mt-1 text-[10px] text-gray-400">
                                        Files shared in the chat will appear here.
                                    </p>
                                </div>
                            </div>
                        ) : (
                            <div className="space-y-2">
                                {groupedAttachments.map((attachment) => {
                                    const canPreview = isViewableFile(attachment.name);
                                    return (
                                        <div
                                            key={attachment.id}
                                            className="flex items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 transition hover:border-[#186cb2]/30 hover:bg-[#186cb2]/5"
                                        >
                                            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-white shadow-sm">
                                                {getFileIcon(attachment.name)}
                                            </div>
                                            <div className="min-w-0 flex-1">
                                                <p
                                                    className="truncate text-xs font-medium text-gray-800"
                                                    title={attachment.name}
                                                >
                                                    {attachment.name}
                                                </p>
                                                <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[9px] text-gray-400">
                                                    {attachment.uploadedBy && (
                                                        <span>{attachment.uploadedBy}</span>
                                                    )}
                                                    {attachment.uploadedAt && (
                                                        <>
                                                            {attachment.uploadedBy && <span>•</span>}
                                                            <span>
                                                                {formatDateTimeChatMessage(attachment.uploadedAt)}
                                                            </span>
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="flex shrink-0 items-center gap-1.5">
                                                {canPreview && (
                                                    <button
                                                        type="button"
                                                        onClick={() => handlePreview(attachment)}
                                                        disabled={isLoadingPreview && previewAttachment?.id === attachment.id}
                                                        className="flex h-8 items-center gap-1.5 rounded-md border border-[#186cb2]/20 bg-white px-2.5 text-[10px] font-medium text-[#186cb2] transition hover:border-[#186cb2]/40 hover:bg-[#186cb2]/5 disabled:cursor-not-allowed disabled:opacity-50"
                                                        title="Preview attachment"
                                                    >
                                                        {isLoadingPreview && previewAttachment?.id === attachment.id ? (
                                                            <svg
                                                                className="h-3.5 w-3.5 animate-spin"
                                                                viewBox="0 0 24 24"
                                                                fill="none"
                                                            >
                                                                <circle
                                                                    cx="12"
                                                                    cy="12"
                                                                    r="9"
                                                                    stroke="currentColor"
                                                                    strokeWidth="2"
                                                                    className="opacity-25"
                                                                />
                                                                <path
                                                                    d="M21 12a9 9 0 0 0-9-9"
                                                                    stroke="currentColor"
                                                                    strokeWidth="2"
                                                                    strokeLinecap="round"
                                                                />
                                                            </svg>
                                                        ) : (
                                                            <svg
                                                                viewBox="0 0 24 24"
                                                                fill="none"
                                                                stroke="currentColor"
                                                                strokeWidth="1.8"
                                                                className="h-3.5 w-3.5"
                                                            >
                                                                <path
                                                                    strokeLinecap="round"
                                                                    strokeLinejoin="round"
                                                                    d="M2.5 12s3.5-5.5 9.5-5.5 9.5 5.5 9.5 5.5-3.5 5.5-9.5 5.5S2.5 12 2.5 12Z"
                                                                />
                                                                <circle
                                                                    cx="12"
                                                                    cy="12"
                                                                    r="2.5"
                                                                />
                                                            </svg>
                                                        )}
                                                        <span className="hidden sm:inline">
                                                            Preview
                                                        </span>
                                                    </button>
                                                )}
                                                <button
                                                    type="button"
                                                    onClick={() => onDownload(attachment)}
                                                    className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-gray-200 bg-white text-gray-500 transition hover:border-[#186cb2]/30 hover:bg-[#186cb2]/5 hover:text-[#186cb2]"
                                                    title="Download attachment"
                                                >
                                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-4 w-4">
                                                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v12" />
                                                        <path strokeLinecap="round" strokeLinejoin="round" d="m7 10 5 5 5-5" />
                                                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 21h14" />
                                                    </svg>
                                                </button>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                    <div className="flex shrink-0 justify-end border-t border-gray-200 bg-gray-50 px-3 py-2">
                        <button
                            type="button"
                            onClick={onClose}
                            className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-[10px] font-medium text-gray-600 transition hover:bg-gray-100"
                        >
                            Close
                        </button>
                    </div>
                </div>
            </div>

            {previewAttachment && isImagePreview && (
                <ImageAttachmentPreviewModal
                    isOpen={Boolean(previewAttachment && previewUrl)}
                    imageUrl={previewUrl}
                    fileName={previewAttachment.name}
                    onClose={handleClosePreview}
                />
            )}

            {previewAttachment && !isImagePreview && (
                <FileAttachmentPreviewModal
                    isOpen={Boolean(previewAttachment)}
                    fileUrl={previewUrl}
                    fileName={previewAttachment.name}
                    fileType={previewAttachment.type}
                    isLoading={isLoadingPreview}
                    onClose={handleClosePreview}
                />
            )}
        </>
    );
}